import * as React from 'react'
import { cn } from '../lib/utils'

export interface BisZumEndeProps {
  children: React.ReactNode
  /** Wird einmal `true`, sobald das Ende sichtbar war — und bleibt es. */
  onGelesen: (gelesen: boolean) => void
  /** Hinweis, solange das Ende nicht erreicht ist. */
  hinweis?: string
  className?: string
}

/**
 * AP-46 9., BD-41 — **Erst lesen, dann bestätigen.** Ein Behälter mit eigener Bildlaufleiste, der meldet,
 * sobald sein Ende sichtbar war. Die Vorzeigeansicht schaltet „Bestätigen" erst danach frei: Der Kunde
 * hat den ganzen Inhalt gesehen, nicht nur den Anfang. Passt der Inhalt ohne Bildlauf, gilt er sofort
 * als gelesen.
 *
 * Gemessen wird über eine Marke am Ende (`IntersectionObserver`) und zusätzlich über die Bildlaufposition
 * mit etwas Spielraum — auf iOS federt der Rand, und die Marke liegt am Telefon oft auf Bruchteilen eines
 * Pixels. Mit `threshold: 1` wurde sie dort nie „ganz" sichtbar, und „Bestätigen" blieb grau.
 */
const SPIELRAUM_PX = 8

function amEnde(el: HTMLElement): boolean {
  return el.scrollHeight - el.scrollTop - el.clientHeight <= SPIELRAUM_PX
}

export function BisZumEnde({ children, onGelesen, hinweis, className }: BisZumEndeProps) {
  const behaelter = React.useRef<HTMLDivElement>(null)
  const marke = React.useRef<HTMLDivElement>(null)
  const [gelesen, setGelesen] = React.useState(false)
  const onGelesenRef = React.useRef(onGelesen)
  onGelesenRef.current = onGelesen

  React.useEffect(() => {
    onGelesenRef.current(gelesen)
  }, [gelesen])

  React.useEffect(() => {
    const ziel = marke.current
    const wurzel = behaelter.current
    if (!ziel || !wurzel || gelesen) return
    if (typeof IntersectionObserver === 'undefined') {
      setGelesen(true)
      return
    }
    const beobachter = new IntersectionObserver(
      (eintraege) => {
        if (eintraege.some((e) => e.isIntersecting)) setGelesen(true)
      },
      // Irgendein Teil der Marke reicht — sie ist nur einen Pixel hoch.
      { root: wurzel, threshold: 0 },
    )
    beobachter.observe(ziel)
    return () => beobachter.disconnect()
  }, [gelesen])

  return (
    <div className="space-y-2">
      <div
        ref={behaelter}
        className={cn('max-h-[60vh] overflow-y-auto overscroll-contain rounded-lg border bg-background', className)}
        tabIndex={0}
        onScroll={(e) => {
          if (!gelesen && amEnde(e.currentTarget)) setGelesen(true)
        }}
      >
        {children}
        <div ref={marke} className="h-px" aria-hidden="true" />
      </div>
      {!gelesen && hinweis && <p className="text-center text-sm text-muted-foreground">{hinweis}</p>}
    </div>
  )
}
